'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { parseAuthIdentifier, IDENTIFIER_HELP } from '@/lib/auth-identifier';
import { getPasswordChecks, isValidSignupPassword, PASSWORD_REQUIREMENTS } from '@/lib/password-validation';
import {
  EyeIcon,
  CheckCircleIcon,
  EyeSlashIcon,
  LockClosedIcon,
  EnvelopeIcon,
  UserIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';

export default function SignupPage() {
  const router = useRouter();
  const { signUp } = useAuth();
  const [fullName, setFullName] = useState('');
  const [inviterId, setInviterId] = useState('');
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref && /^[a-f0-9-]{36}$/i.test(ref)) setInviterId(ref);
  }, []);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [identifierTouched, setIdentifierTouched] = useState(false);
  const validIdentifier = Boolean(parseAuthIdentifier(identifier));
  const validPassword = isValidSignupPassword(password);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const identifierError = validIdentifier || (!identifierTouched && !identifier)
    ? ''
    : !identifier.trim()
      ? 'Enter your email or mobile number.'
      : identifier.includes('@')
        ? 'Enter a valid email address, such as you@example.com.'
        : /^[+0-9 ()-]+$/.test(identifier.trim())
          ? 'Enter a valid 10-digit Indian mobile number or an international mobile number with +country code.'
          : 'Enter a valid email address or mobile number.';

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!parseAuthIdentifier(identifier)) {
      setError(IDENTIFIER_HELP);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!isValidSignupPassword(password)) {
      setError(PASSWORD_REQUIREMENTS);
      return;
    }

    setLoading(true);
    try {
      await signUp(identifier, password, { fullName, inviterId });
      router.push('/');
      router.refresh();
    } catch (err: any) {
      setError(err?.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Background blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="blob-primary absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-40" />
        <div className="blob-green absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-30" />
      </div>

      <div className="relative w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl gradient-blue flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/20">
            <UserPlusIcon style={{ width: 32, height: 32 }} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Join BizzBox</h1>
          <p className="text-muted-foreground text-sm mt-1">Create your community account</p>
        </div>

        {/* Signup Card */}
        <div className="bg-card border border-border rounded-2xl p-6 shadow-xl">
          <form onSubmit={handleSignup} className="space-y-4">
            {inviterId && <p className="rounded-xl bg-primary/10 p-3 text-sm text-primary">You’re signing up through an invite. Creating your account will automatically add the inviter as your friend.</p>}
            {/* Full Name */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                Full Name
              </label>
              <div className="relative">
                <UserIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  style={{ width: 16, height: 16 }}
                />
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your full name"
                  required
                  className="w-full bg-input border border-border rounded-xl pl-10 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                />
              </div>
            </div>

            {/* Email or mobile */}
            <div>
              <label htmlFor="auth-identifier" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                Email or mobile number
              </label>
              <div className="relative">
                <EnvelopeIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  style={{ width: 16, height: 16 }}
                />
                <input
                  type="text"
                  id="auth-identifier"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  aria-describedby={identifierError ? 'identifier-error identifier-help' : 'identifier-help'}
                  aria-invalid={Boolean(identifierError)}
                  maxLength={254}
                  onBlur={() => setIdentifierTouched(true)}
                  value={identifier}
                  onChange={(e) => { setIdentifier(e.target.value); setError(''); }}
                  placeholder="Email or mobile number"
                  required
                  className={`w-full bg-input border rounded-xl pl-10 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 transition-all ${identifierError ? 'border-red-500 focus:ring-red-500/50' : 'border-border focus:ring-primary/50 focus:border-primary/50'}`}
                />
              </div>
            </div>

            {identifierError && <p id="identifier-error" role="status" className="text-xs text-red-500">{identifierError}</p>}
            <p id="identifier-help" className="text-xs text-muted-foreground">{IDENTIFIER_HELP}</p>

            {/* Password */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                Password
              </label>
              <div className="relative">
                <LockClosedIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  style={{ width: 16, height: 16 }}
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="Min. 8 characters"
                  minLength={8}
                  autoComplete="new-password"
                  aria-describedby="password-requirements"
                  required
                  className="w-full bg-input border border-border rounded-xl pl-10 pr-10 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? (
                    <EyeSlashIcon style={{ width: 16, height: 16 }} />
                  ) : (
                    <EyeIcon style={{ width: 16, height: 16 }} />
                  )}
                </button>
              </div>
            </div>

            <ul id="password-requirements" className="space-y-1.5 text-xs" aria-live="polite" aria-label="Password requirements">
              {getPasswordChecks(password).map((check) => (
                <li key={check.label} className={`flex items-center gap-2 ${check.met ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                  {check.met ? (
                    <CheckCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  ) : (
                    <span className="h-4 w-4 shrink-0 rounded-full border border-current opacity-50" aria-hidden="true" />
                  )}
                  <span><span className="sr-only">{check.met ? 'Met: ' : 'Required: '}</span>{check.label}</span>
                </li>
              ))}
            </ul>

            {/* Confirm Password */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                Confirm Password
              </label>
              <div className="relative">
                <LockClosedIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  style={{ width: 16, height: 16 }}
                />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  autoComplete="new-password"
                  aria-invalid={Boolean(confirmPassword) && !passwordsMatch}
                  aria-describedby={confirmPassword ? 'password-match-status' : undefined}
                  placeholder="Repeat your password"
                  required
                  className="w-full bg-input border border-border rounded-xl pl-10 pr-10 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon style={{ width: 16, height: 16 }} />
                  ) : (
                    <EyeIcon style={{ width: 16, height: 16 }} />
                  )}
                </button>
              </div>
            </div>

            {confirmPassword && (
              <p id="password-match-status" role="status" className={`flex items-center gap-2 text-xs ${passwordsMatch ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                {passwordsMatch && <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />}
                {passwordsMatch ? 'Passwords match.' : 'Passwords do not match.'}
              </p>
            )}

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-xs text-red-400">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !validIdentifier || !validPassword || !passwordsMatch}
              className="w-full py-3.5 rounded-xl gradient-blue text-white font-bold text-sm flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-60"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="mt-5 pt-4 border-t border-border text-center">
            <p className="text-xs text-muted-foreground">
              Already have an account?{' '}
              <Link href="/login" className="text-primary font-semibold hover:underline">
                Sign In
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-[11px] text-muted-foreground mt-4 px-4">
          By creating an account, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
